import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import Text from '@cartwhl/ui/base/Text';
import EyeIcon from '@cartwhl/ui/icons/EyeIcon';
import HandIcon from '@cartwhl/ui/icons/HandIcon';
import HistoryIcon from '@cartwhl/ui/icons/HistoryIcon';
import MotionIcon from '@cartwhl/ui/icons/MotionIcon';
import RotateIcon from '@cartwhl/ui/icons/RotateIcon';

import type {
  PropertiesToolMode,
  RotateSpaceMode,
  ViewerToolMode,
} from '../../cartographerStore';
import {
  ExportMotionIcon,
  MappingIcon,
  MAPPING_ICON_SWEEP_MS,
} from '../../presentations/icons';
import PropertiesIcon from '../../presentations/icons/PropertiesIcon';
import type { BonePropertyIndicatorState } from '../../presentations/properties';
import type { BoneAssignmentMode } from '../BoneAssignments';

const DOCK_PANEL_CLASS =
  'dark:bg-gray-90/40 bg-gray-15/45 rounded-2xl p-1.5 backdrop-blur-md';
const DOCK_BUTTON_BASE_CLASS =
  'focus-visible inline-flex h-9 items-center rounded-xl transition-all duration-150';
const DOCK_MICRO_BUTTON_BASE_CLASS =
  'focus-visible inline-flex h-[17px] w-full items-center justify-center rounded-md px-2 transition-all duration-150';
const DOCK_ICON_SLOT_CLASS =
  'flex h-[22px] w-[22px] items-center justify-center';
const DOCK_BUTTON_ACTIVE_CLASS =
  'dark:bg-gray-90 bg-white text-gray-90 dark:text-gray-15';
const DOCK_BUTTON_INACTIVE_CLASS =
  'bg-gray-15/25 text-gray-90 dark:bg-gray-90/25 dark:text-gray-15 hover:bg-gray-15 dark:hover:bg-gray-90';
const DOCK_ICON_ONLY_THRESHOLD_PX = 920;
const DOCK_OPTION_INITIALS_THRESHOLD_PX = 760;
const PROPERTY_OPTION_DOT_CLASS =
  'bg-current pointer-events-none absolute bottom-[5px] left-1/2 z-[1] block h-1.5 w-1.5 -translate-x-1/2 rounded-full transition-opacity duration-150';

export interface ViewerToolDockMappingSectionProps {
  mappingOptionsOpen: boolean;
  mappingRunAnimationToken: number;
  mappingRunning?: boolean;
  mappingHasAssignments: boolean;
  mappingDisabled?: boolean;
  runMappingDisabled?: boolean;
  clearMappingDisabled?: boolean;
  boneAssignmentsOpen: boolean;
  boneAssignmentsDisabled?: boolean;
  boneAssignmentsMode: BoneAssignmentMode;
  onMappingOptionsToggle: () => void;
  onRunMapping: () => void;
  onClearMapping: () => void;
  onBoneAssignmentsToggle: () => void;
  onBoneAssignmentsModeChange: (mode: BoneAssignmentMode) => void;
}

export interface ViewerToolDockMotionSectionProps {
  onChooseMotionClick: () => void;
  chooseMotionDisabled?: boolean;
  chooseMotionTitle?: string;
  previewVisible?: boolean;
  onPreviewClick?: () => void;
  previewDisabled?: boolean;
  previewTitle?: string;
  previewActive?: boolean;
  previewOptions?: Array<{
    id: string;
    label: string;
    compactLabel?: string;
    active: boolean;
    onClick: () => void;
    tooltipText: string;
    ariaLabel?: string;
    disabled?: boolean;
  }>;
  previewOptionsOpen?: boolean;
  previewExtension?: ReactNode;
  previewExtensionOpen?: boolean;
  exportVisible?: boolean;
  onExportClick?: () => void;
  exportDisabled?: boolean;
  exportBusy?: boolean;
  exportTitle?: string;
}

export interface ViewerToolDockHistorySectionProps {
  historyOpen: boolean;
  onHistoryToggle: () => void;
}

interface ViewerToolDockProps {
  availableWidth?: number;
  forceIconOnly?: boolean;
  viewerToolMode: ViewerToolMode;
  rotateSpaceMode: RotateSpaceMode;
  propertiesToolMode: PropertiesToolMode;
  selectedBonePropertyIndicators: BonePropertyIndicatorState;
  historySection?: ViewerToolDockHistorySectionProps;
  mappingSection?: ViewerToolDockMappingSectionProps;
  motionSection?: ViewerToolDockMotionSectionProps;
  onAnyButtonClick?: () => void;
  onViewerToolModeChange: (mode: ViewerToolMode) => void;
  onRotateSpaceModeChange: (mode: RotateSpaceMode) => void;
  onPropertiesToolModeChange: (mode: PropertiesToolMode) => void;
}

interface DockToolButtonProps {
  label: string;
  showLabel?: boolean;
  active: boolean;
  onClick: () => void;
  tooltipText: string;
  icon: ReactNode;
  disabled?: boolean;
}

interface DockToolOption {
  id: string;
  label: string;
  compactLabel?: string;
  compact?: boolean;
  active: boolean;
  onClick: () => void;
  tooltipText: string;
  ariaLabel?: string;
  disabled?: boolean;
  indicatorVisible?: boolean;
}

interface DockMicroToggleProps {
  options: DockToolOption[];
}

interface DockToolWithOptionsProps extends DockToolButtonProps {
  options?: DockToolOption[];
  showOptions?: boolean;
  useCompactOptionLabels?: boolean;
  extensionContent?: ReactNode;
  extensionOpen?: boolean;
}

interface DockToolConfig extends DockToolWithOptionsProps {
  id: string;
}

interface DockToolsPanelProps {
  tools: DockToolConfig[];
  showToolLabels: boolean;
  useCompactOptionLabels: boolean;
  stackedExtensionContent?: ReactNode;
  stackedExtensionOpen?: boolean;
}

const GROUP_BACKGROUND_SYNC_MS = 200;

function DockToolButton({
  label,
  showLabel = true,
  active,
  onClick,
  tooltipText,
  icon,
  disabled = false,
}: DockToolButtonProps) {
  return (
    <Tooltip text={tooltipText} side="top" inModal>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={`${DOCK_BUTTON_BASE_CLASS} ${showLabel ? 'gap-2 px-3' : 'px-2'} ${active ? DOCK_BUTTON_ACTIVE_CLASS : DOCK_BUTTON_INACTIVE_CLASS} ${disabled ? 'cursor-not-allowed opacity-40' : ''}`}
        aria-pressed={active}
        aria-label={label}
      >
        <span className={DOCK_ICON_SLOT_CLASS}>{icon}</span>
        {showLabel && (
          <Text as="span" variant="studio/body/medium/sm">
            {label}
          </Text>
        )}
      </button>
    </Tooltip>
  );
}

function DockToolOptionButton({
  label,
  compactLabel,
  active,
  onClick,
  tooltipText,
  ariaLabel,
  disabled = false,
  compact = false,
  showText = true,
  indicatorVisible = false,
}: DockToolOption & { showText?: boolean }) {
  const displayLabel =
    compact ? (compactLabel ?? label.charAt(0)) : label;

  return (
    <Tooltip text={tooltipText} side="top" inModal>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={`${DOCK_BUTTON_BASE_CLASS} ${compact ? 'px-2' : 'px-3'} relative overflow-hidden ${active ? DOCK_BUTTON_ACTIVE_CLASS : DOCK_BUTTON_INACTIVE_CLASS} ${disabled ? 'cursor-not-allowed opacity-40' : ''}`}
        aria-pressed={active}
        aria-label={ariaLabel ?? label}
      >
        <Text
          as="span"
          variant="studio/body/medium/sm"
          className={`whitespace-nowrap transition-opacity duration-150 ${showText ? 'opacity-100' : 'opacity-0'}`}
        >
          {displayLabel}
        </Text>
        <span
          className={`${PROPERTY_OPTION_DOT_CLASS} ${showText && indicatorVisible ? 'opacity-100' : 'opacity-0'}`}
          aria-hidden="true"
        />
      </button>
    </Tooltip>
  );
}

interface BoneAssignmentsModeDockToggleProps {
  mode: BoneAssignmentMode;
  onModeChange: (mode: BoneAssignmentMode) => void;
}

interface BoneAssignmentsModeDockToggleButtonProps {
  label: 'Roles' | 'Chains';
  active: boolean;
  onClick: () => void;
}

function BoneAssignmentsModeDockToggleButton({
  label,
  active,
  onClick,
}: BoneAssignmentsModeDockToggleButtonProps) {
  return (
    <Tooltip
      text={`Show ${label.toLowerCase()} assignment mode`}
      side="top"
      inModal
    >
      <button
        type="button"
        onClick={onClick}
        className={`${DOCK_MICRO_BUTTON_BASE_CLASS} ${active ? DOCK_BUTTON_ACTIVE_CLASS : DOCK_BUTTON_INACTIVE_CLASS}`}
        aria-pressed={active}
        aria-label={`Show ${label.toLowerCase()} assignment mode`}
      >
        <Text
          as="span"
          variant="studio/body/medium/xs"
          className="relative top-px flex h-full items-center justify-center leading-none whitespace-nowrap"
        >
          {label}
        </Text>
      </button>
    </Tooltip>
  );
}

function BoneAssignmentsModeDockToggle({
  mode,
  onModeChange,
}: BoneAssignmentsModeDockToggleProps) {
  return (
    <div className="flex w-[68px] flex-col gap-[2px]">
      <BoneAssignmentsModeDockToggleButton
        label="Roles"
        active={mode === 'roles'}
        onClick={() => onModeChange('roles')}
      />
      <BoneAssignmentsModeDockToggleButton
        label="Chains"
        active={mode === 'chains'}
        onClick={() => onModeChange('chains')}
      />
    </div>
  );
}

function DockMicroToggle({ options }: DockMicroToggleProps) {
  return (
    <div className="flex w-[68px] flex-col gap-[2px]">
      {options.map((option) => (
        <Tooltip key={option.id} text={option.tooltipText} side="top" inModal>
          <button
            type="button"
            onClick={option.onClick}
            disabled={option.disabled}
            className={`${DOCK_MICRO_BUTTON_BASE_CLASS} ${option.active ? DOCK_BUTTON_ACTIVE_CLASS : DOCK_BUTTON_INACTIVE_CLASS} ${option.disabled ? 'cursor-not-allowed opacity-40' : ''}`}
            aria-pressed={option.active}
            aria-label={option.ariaLabel ?? option.label}
          >
            <Text
              as="span"
              variant="studio/body/medium/xs"
              className="relative top-px flex h-full items-center justify-center leading-none whitespace-nowrap"
            >
              {option.label}
            </Text>
          </button>
        </Tooltip>
      ))}
    </div>
  );
}

interface MappingToolIconProps {
  runToken: number;
  hasAssignments: boolean;
}

function MappingToolIcon({ runToken, hasAssignments }: MappingToolIconProps) {
  const [sweepProgress, setSweepProgress] = useState(1);
  const [dotVisible, setDotVisible] = useState(hasAssignments);
  const [isResetting, setIsResetting] = useState(false);
  const [isSweeping, setIsSweeping] = useState(false);
  const hasAssignmentsRef = useRef(hasAssignments);
  const mountedRef = useRef(false);
  const sweepRafRef = useRef<number | null>(null);
  const sweepTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    hasAssignmentsRef.current = hasAssignments;
    if (!hasAssignments) {
      setDotVisible(false);
      return;
    }
    if (!isSweeping) {
      setSweepProgress(1);
      setDotVisible(true);
    }
  }, [hasAssignments, isSweeping]);

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }

    if (sweepRafRef.current !== null) {
      cancelAnimationFrame(sweepRafRef.current);
      sweepRafRef.current = null;
    }
    if (sweepTimeoutRef.current !== null) {
      clearTimeout(sweepTimeoutRef.current);
      sweepTimeoutRef.current = null;
    }

    setIsResetting(true);
    setIsSweeping(false);
    setDotVisible(false);
    setSweepProgress(0);

    // Reset ring immediately, then start sweep on the next paint frame.
    sweepRafRef.current = requestAnimationFrame(() => {
      sweepRafRef.current = null;
      setIsResetting(false);
      setIsSweeping(true);
      setSweepProgress(1);

      sweepTimeoutRef.current = window.setTimeout(() => {
        sweepTimeoutRef.current = null;
        setIsSweeping(false);
        if (hasAssignmentsRef.current) {
          setDotVisible(true);
        }
      }, MAPPING_ICON_SWEEP_MS);
    });
  }, [runToken]);

  useEffect(
    () => () => {
      if (sweepRafRef.current !== null) {
        cancelAnimationFrame(sweepRafRef.current);
      }
      if (sweepTimeoutRef.current !== null) {
        clearTimeout(sweepTimeoutRef.current);
      }
    },
    [],
  );

  return (
    <MappingIcon
      className="h-[22px] w-[22px]"
      dotVisible={dotVisible}
      ringOpacity={isResetting ? 0 : 1}
      ringTransition={
        isSweeping
          ? `stroke-dashoffset ${MAPPING_ICON_SWEEP_MS}ms cubic-bezier(0.22, 1, 0.36, 1), opacity 120ms ease-out`
          : 'opacity 120ms ease-out'
      }
      sweepProgress={sweepProgress}
    />
  );
}

function DockToolWithOptions({
  label,
  active,
  onClick,
  tooltipText,
  icon,
  disabled = false,
  showLabel = true,
  options = [],
  showOptions = false,
  useCompactOptionLabels = false,
  extensionContent,
  extensionOpen = false,
}: DockToolWithOptionsProps) {
  const hasOptions = options.length > 0;
  const optionsVisible = hasOptions && showOptions;
  const [groupBackgroundVisible, setGroupBackgroundVisible] =
    useState(optionsVisible);
  const [optionLabelsVisible, setOptionLabelsVisible] =
    useState(optionsVisible);
  const extensionVisible = Boolean(extensionContent && extensionOpen);
  const dockGroupExpanded = groupBackgroundVisible || extensionVisible;
  const hideBackgroundTimeoutRef = useRef<number | null>(null);
  const showLabelsTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (hideBackgroundTimeoutRef.current !== null) {
      clearTimeout(hideBackgroundTimeoutRef.current);
      hideBackgroundTimeoutRef.current = null;
    }

    if (optionsVisible) {
      setGroupBackgroundVisible(true);
      return;
    }

    hideBackgroundTimeoutRef.current = window.setTimeout(() => {
      hideBackgroundTimeoutRef.current = null;
      setGroupBackgroundVisible(false);
    }, GROUP_BACKGROUND_SYNC_MS);
  }, [optionsVisible]);

  useEffect(() => {
    if (showLabelsTimeoutRef.current !== null) {
      clearTimeout(showLabelsTimeoutRef.current);
      showLabelsTimeoutRef.current = null;
    }

    if (!optionsVisible) {
      setOptionLabelsVisible(false);
      return;
    }

    setOptionLabelsVisible(false);
    showLabelsTimeoutRef.current = window.setTimeout(() => {
      showLabelsTimeoutRef.current = null;
      setOptionLabelsVisible(true);
    }, GROUP_BACKGROUND_SYNC_MS);
  }, [optionsVisible]);

  useEffect(
    () => () => {
      if (hideBackgroundTimeoutRef.current !== null) {
        clearTimeout(hideBackgroundTimeoutRef.current);
      }
      if (showLabelsTimeoutRef.current !== null) {
        clearTimeout(showLabelsTimeoutRef.current);
      }
    },
    [],
  );

  return (
    <div
      className={`relative flex items-end rounded-lg ${dockGroupExpanded ? 'gap-1 px-1.5' : 'gap-0'}`}
    >
      {(hasOptions && groupBackgroundVisible) || extensionVisible ? (
        <div className="bg-gray-60/40 pointer-events-none absolute inset-x-0 -inset-y-1 rounded-lg shadow-inner dark:bg-black/40" />
      ) : null}

      <div
        className={`relative z-10 flex items-end ${dockGroupExpanded ? 'gap-1' : 'gap-0'}`}
      >
        <DockToolButton
          label={label}
          active={active}
          onClick={onClick}
          tooltipText={tooltipText}
          icon={icon}
          disabled={disabled}
          showLabel={showLabel}
        />

        {hasOptions && (
          <div
            className={`grid overflow-hidden transition-all duration-200 ease-out ${optionsVisible ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
            style={{ gridTemplateColumns: optionsVisible ? '1fr' : '0fr' }}
            aria-hidden={!optionsVisible}
          >
            <div className="min-w-0 overflow-hidden">
              <div className="flex h-9 items-center gap-1 rounded-lg pr-0 pl-1">
                {options.map((option) => (
                  <DockToolOptionButton
                    key={option.id}
                    id={option.id}
                    label={option.label}
                    active={option.active}
                    onClick={option.onClick}
                    tooltipText={option.tooltipText}
                    ariaLabel={option.ariaLabel}
                    compactLabel={option.compactLabel}
                    compact={useCompactOptionLabels}
                    showText={optionLabelsVisible}
                    indicatorVisible={option.indicatorVisible}
                    disabled={!optionsVisible || option.disabled}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {extensionContent && (
          <div
            className={`grid overflow-hidden transition-all duration-200 ease-out ${extensionVisible ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
            style={{ gridTemplateColumns: extensionVisible ? '1fr' : '0fr' }}
            aria-hidden={!extensionVisible}
          >
            <div className="min-w-0 overflow-hidden">
              <div className="flex items-end pl-1">{extensionContent}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DockToolsPanel({
  tools,
  showToolLabels,
  useCompactOptionLabels,
  stackedExtensionContent,
  stackedExtensionOpen = false,
}: DockToolsPanelProps) {
  if (tools.length === 0) return null;

  const hasStackedExtension = Boolean(stackedExtensionContent);
  const stackedExtensionVisible = hasStackedExtension && stackedExtensionOpen;

  return (
    <div className="relative">
      {hasStackedExtension && (
        <div
          className={`pointer-events-none absolute right-0 bottom-full mb-1 origin-bottom-right transition-[opacity,transform] duration-200 ease-out ${
            stackedExtensionVisible
              ? 'translate-y-0 scale-100 opacity-100'
              : 'translate-y-2 scale-95 opacity-0'
          }`}
          aria-hidden={!stackedExtensionVisible}
        >
          <div
            className={`w-[264px] max-w-[78vw] ${
              stackedExtensionVisible
                ? 'pointer-events-auto'
                : 'pointer-events-none'
            }`}
          >
            {stackedExtensionContent}
          </div>
        </div>
      )}

      <div className={DOCK_PANEL_CLASS}>
        <div className="flex items-center gap-1">
          {tools.map((tool) => (
            <DockToolWithOptions
              key={tool.id}
              label={tool.label}
              showLabel={showToolLabels && (tool.showLabel ?? true)}
              active={tool.active}
              onClick={tool.onClick}
              tooltipText={tool.tooltipText}
              icon={tool.icon}
              disabled={tool.disabled}
              options={tool.options}
              showOptions={tool.showOptions}
              useCompactOptionLabels={useCompactOptionLabels}
              extensionContent={tool.extensionContent}
              extensionOpen={tool.extensionOpen}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ViewerToolDock({
  availableWidth = 0,
  forceIconOnly = false,
  viewerToolMode,
  rotateSpaceMode,
  propertiesToolMode,
  selectedBonePropertyIndicators,
  historySection,
  mappingSection,
  motionSection,
  onAnyButtonClick,
  onViewerToolModeChange,
  onRotateSpaceModeChange,
  onPropertiesToolModeChange,
}: ViewerToolDockProps) {
  const handleDockClickCapture = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!onAnyButtonClick) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (!target.closest('button')) return;
      onAnyButtonClick();
    },
    [onAnyButtonClick],
  );
  const showToolLabels =
    !forceIconOnly &&
    (availableWidth === 0 || availableWidth >= DOCK_ICON_ONLY_THRESHOLD_PX);
  const showCoreToolLabels = showToolLabels && !mappingSection?.mappingOptionsOpen;
  const useCompactOptionLabels =
    forceIconOnly ||
    (availableWidth > 0 && availableWidth < DOCK_OPTION_INITIALS_THRESHOLD_PX);
  const rotateActive = viewerToolMode === 'rotate';
  const propertiesActive = viewerToolMode === 'properties';
  const transformToolActive = rotateActive || propertiesActive;
  const tools: DockToolConfig[] = [
    {
      id: 'select-tool',
      label: 'Select',
      active: viewerToolMode === 'select',
      tooltipText: 'Selection only (no transform)',
      onClick: () => onViewerToolModeChange('select'),
      icon: <HandIcon className="h-[22px] w-[22px]" />,
    },
    {
      id: 'rotate-tool',
      label: 'Rotate',
      active: rotateActive,
      tooltipText: 'Rotate selected bone in viewport',
      onClick: () => onViewerToolModeChange(rotateActive ? 'select' : 'rotate'),
      icon: <RotateIcon className="h-[22px] w-[22px]" />,
      extensionContent: (
        <DockMicroToggle
          options={[
            {
              id: 'rotate-local',
              label: 'Local',
              compactLabel: 'L',
              active: rotateSpaceMode === 'local',
              tooltipText: 'Rotate in local space',
              onClick: () => onRotateSpaceModeChange('local'),
              ariaLabel: 'Local rotation mode',
            },
            {
              id: 'rotate-gimbal',
              label: 'Gimbal',
              compactLabel: 'G',
              active: rotateSpaceMode === 'gimbal',
              tooltipText: 'Rotate using gimbal (Euler-order) axes',
              onClick: () => onRotateSpaceModeChange('gimbal'),
              ariaLabel: 'Gimbal rotation mode',
            },
          ]}
        />
      ),
      extensionOpen: rotateActive,
    },
    {
      id: 'properties-tool',
      label: 'Properties',
      active: propertiesActive,
      tooltipText: 'Edit selected bone properties in viewport',
      onClick: () =>
        onViewerToolModeChange(propertiesActive ? 'select' : 'properties'),
      icon: <PropertiesIcon className="h-[22px] w-[22px]" />,
      showOptions: propertiesActive,
      options: [
        {
          id: 'properties-limits',
          label: 'Limits',
          compactLabel: 'Lim',
          active: propertiesToolMode === 'limits',
          tooltipText: 'Adjust bone rotation limits in viewport',
          onClick: () => onPropertiesToolModeChange('limits'),
          ariaLabel: 'Rotation limits mode',
          indicatorVisible: selectedBonePropertyIndicators.limits,
        },
        {
          id: 'properties-angle-bias',
          label: 'Angle Bias',
          compactLabel: 'AB',
          active: propertiesToolMode === 'angle-bias',
          tooltipText: 'Adjust Preferred Angle Bias',
          onClick: () => onPropertiesToolModeChange('angle-bias'),
          ariaLabel: 'Angle bias mode',
          indicatorVisible: selectedBonePropertyIndicators.angleBias,
        },
        {
          id: 'properties-offset',
          label: 'Offset',
          compactLabel: 'Off',
          active: propertiesToolMode === 'offset',
          tooltipText:
            'Adjust stored bone rotate offsets used as a post-process on retargeted motion',
          onClick: () => onPropertiesToolModeChange('offset'),
          ariaLabel: 'Bone offset mode',
          indicatorVisible: selectedBonePropertyIndicators.postOffset,
        },
      ],
    },
  ];

  if (historySection) {
    tools.splice(0, 0, {
      id: 'history-tool',
      label: 'History',
      active: historySection.historyOpen,
      tooltipText: historySection.historyOpen
        ? 'Hide history panel'
        : 'Show undo/redo history',
      onClick: historySection.onHistoryToggle,
      icon: <HistoryIcon className="h-[18px] w-[18px]" />,
      showLabel: !transformToolActive,
    });
  }

  if (mappingSection) {
    const {
      mappingOptionsOpen,
      mappingRunAnimationToken,
      mappingRunning = false,
      mappingHasAssignments,
      mappingDisabled = false,
      runMappingDisabled = false,
      clearMappingDisabled = false,
      boneAssignmentsOpen,
      boneAssignmentsDisabled = false,
      boneAssignmentsMode,
      onMappingOptionsToggle,
      onRunMapping,
      onClearMapping,
      onBoneAssignmentsToggle,
      onBoneAssignmentsModeChange,
    } = mappingSection;

    tools.splice(2, 0, {
      id: 'mapping-tool',
      label: 'Mapping',
      active: mappingOptionsOpen,
      tooltipText: mappingDisabled
        ? 'Load a rigged skeleton before running mapping recognition'
        : 'Run or clear automatic role and chain recognition',
      onClick: onMappingOptionsToggle,
      icon: (
        <MappingToolIcon
          runToken={mappingRunAnimationToken}
          hasAssignments={mappingHasAssignments}
        />
      ),
      disabled: mappingDisabled,
      showLabel: !transformToolActive,
      showOptions: mappingOptionsOpen,
      extensionContent: (
        <BoneAssignmentsModeDockToggle
          mode={boneAssignmentsMode}
          onModeChange={onBoneAssignmentsModeChange}
        />
      ),
      extensionOpen: boneAssignmentsOpen,
      options: [
        {
          id: 'mapping-run',
          label: 'Run',
          compactLabel: 'R',
          active: mappingRunning,
          tooltipText: mappingRunning
            ? 'Recognition is running…'
            : runMappingDisabled
              ? 'Load a rigged skeleton to run recognition'
              : 'Run automatic role and chain recognition',
          onClick: onRunMapping,
          ariaLabel: 'Run mapping recognition',
          disabled: runMappingDisabled,
        },
        {
          id: 'mapping-clear',
          label: 'Clear',
          compactLabel: 'C',
          active: false,
          tooltipText: clearMappingDisabled
            ? 'No recognized roles or chains to clear'
            : 'Clear recognized roles and chains (keeps properties)',
          onClick: onClearMapping,
          ariaLabel: 'Clear mapping recognition',
          disabled: clearMappingDisabled,
        },
        {
          id: 'mapping-assign',
          label: 'Assign',
          compactLabel: 'A',
          active: boneAssignmentsOpen,
          tooltipText: boneAssignmentsDisabled
            ? 'Load a rigged skeleton before manually assigning bones'
            : boneAssignmentsOpen
              ? 'Return to inspector panel'
              : 'Open manual bone assignments panel',
          onClick: onBoneAssignmentsToggle,
          ariaLabel: 'Toggle manual bone assignments panel',
          disabled: boneAssignmentsDisabled,
        },
      ],
    });
  }

  if (motionSection) {
    const {
      onChooseMotionClick,
      chooseMotionDisabled = false,
      chooseMotionTitle = 'Pick a motion from your Canvas projects to preview on this character',
      previewVisible = false,
      onPreviewClick,
      previewDisabled = false,
      previewTitle = 'Choose where to preview the retargeted motion',
      previewActive = false,
      previewOptions = [],
      previewOptionsOpen = false,
      exportVisible = false,
      onExportClick,
      exportDisabled = false,
      exportBusy = false,
      exportTitle = 'Export the retargeted motion',
    } = motionSection;

    tools.push({
      id: 'add-motion-tool',
      label: 'Motion',
      active: false,
      tooltipText: chooseMotionDisabled
        ? 'Save your character to load and preview motions'
        : chooseMotionTitle,
      onClick: onChooseMotionClick,
      icon: <MotionIcon className="h-[18px] w-[18px]" />,
      disabled: chooseMotionDisabled,
      showLabel: !transformToolActive,
    });

    if (previewVisible) {
      tools.push({
        id: 'preview-motion-tool',
        label: 'Preview',
        active: previewActive,
        tooltipText: previewDisabled
          ? 'Select a motion and load a character to preview it'
          : previewTitle,
        onClick: onPreviewClick ?? (() => {}),
        icon: <EyeIcon className="h-[18px] w-[18px]" />,
        disabled: previewDisabled,
        extensionContent: <DockMicroToggle options={previewOptions} />,
        extensionOpen: previewOptionsOpen,
      });
    }

    if (exportVisible) {
      tools.push({
        id: 'export-motion-tool',
        label: 'Export',
        active: false,
        tooltipText: exportTitle,
        onClick: onExportClick ?? (() => {}),
        icon: (
          <ExportMotionIcon
            className="h-[18px] w-[18px]"
            exporting={exportBusy}
          />
        ),
        disabled: exportDisabled,
      });
    }
  }

  const historyTools = tools.filter(
    (tool) => tool.id === 'history-tool',
  );
  const coreTools = tools.filter(
    (tool) =>
      tool.id === 'select-tool' ||
      tool.id === 'rotate-tool' ||
      tool.id === 'properties-tool',
  );
  const mappingTools = tools.filter((tool) => tool.id === 'mapping-tool');
  const motionTools = tools.filter(
    (tool) =>
      tool.id === 'add-motion-tool' ||
      tool.id === 'preview-motion-tool' ||
      tool.id === 'export-motion-tool',
  );

  return (
    <div
      className="pointer-events-auto w-full min-w-0"
      onClickCapture={handleDockClickCapture}
    >
      <div className="mx-auto flex w-max max-w-full items-end gap-2">
        <DockToolsPanel
          tools={historyTools}
          showToolLabels={showCoreToolLabels}
          useCompactOptionLabels={useCompactOptionLabels}
        />
        <DockToolsPanel
          tools={coreTools}
          showToolLabels={showCoreToolLabels}
          useCompactOptionLabels={useCompactOptionLabels}
        />
        <DockToolsPanel
          tools={mappingTools}
          showToolLabels={showToolLabels}
          useCompactOptionLabels={useCompactOptionLabels}
        />
        <DockToolsPanel
          tools={motionTools}
          showToolLabels={showToolLabels}
          useCompactOptionLabels={useCompactOptionLabels}
          stackedExtensionContent={motionSection?.previewExtension}
          stackedExtensionOpen={motionSection?.previewExtensionOpen}
        />
      </div>
    </div>
  );
}
